/**
 * Single validator for auth resume targets (the `redirect_url` query param
 * that carries a user back to where they were through sign-in → sign-up →
 * email verification → profile completion).
 *
 * Rules (phase 5, area 2 — approved):
 * - The target must be a relative path inside the app: starts with '/',
 *   not protocol-relative ('//…'), no protocol, and it must not be able to
 *   escape the origin through path tricks ('/\\evil.com', '/..//evil.com').
 * - Query params are filtered against an allow-list, so secret-bearing
 *   params (e.g. `email`) never survive the auth round-trip.
 * - Invalid or missing input returns the fallback ('/' by default). Callers
 *   that know the user is signed in pass `{ fallback: '/dashboard' }`.
 */

const ALLOWED_RESUME_PARAMS = new Set([
  // Invite flows — model invite tokens ride inside redirect_url.
  'token',
  // Stripe checkout returns.
  'payment',
  'session_id',
  'canceled',
  // Tabbed pages.
  'tab',
  // Chained auth targets (/sign-up/profile?redirect_url=…). The nested
  // value is itself validated by safeResumeTarget when it is read.
  'redirect_url',
]);

// Dummy base for parsing relative targets; never appears in output.
const PARSE_BASE = 'http://resume.invalid';

export function safeResumeTarget(
  raw: string | null,
  options: { fallback?: string } = {},
): string {
  const fallback = options.fallback ?? '/';

  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return fallback;

  let url: URL;
  try {
    url = new URL(raw, PARSE_BASE);
  } catch {
    return fallback;
  }

  // Backslashes parse like slashes and '..' segments normalize, so a value
  // that passed the prefix checks can still resolve to another host or a
  // protocol-relative path. Reject anything that does not stay a
  // same-origin path.
  if (url.origin !== PARSE_BASE || url.pathname.startsWith('//')) return fallback;

  const params = new URLSearchParams();
  for (const [key, value] of url.searchParams) {
    if (ALLOWED_RESUME_PARAMS.has(key)) params.append(key, value);
  }

  const query = params.toString();
  return `${url.pathname}${query ? `?${query}` : ''}${url.hash}`;
}
